import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Users, 
  Target,
  Compass,
  CheckCircle2,
  ArrowRight,
  Shield,
  Heart,
  Mail,
  Sparkles,
  Sliders
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DinoLogo } from '@/components/DinoLogo';
import { MetaTags } from '@/components/MetaTags';

// Friendly dinosaur mascot component
function DinoMascot({ className = "" }: { className?: string }) {
  return (
    <div className="relative">
      <img 
        src="/dino-logo.png" 
        alt="Dino mascot - your friendly reading companion" 
        className={className}
      />
    </div>
  );
}

// Journey statement component for motivational interstitial copy
function JourneyStatement({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <div className="text-center py-12 md:py-16">
      {icon && <div className="mb-4 flex justify-center">{icon}</div>}
      <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
        {children}
      </p>
    </div>
  );
}

// Pillar card component for the four core pillars
function PillarCard({ 
  icon: Icon, 
  title, 
  subtitle,
  features,
  accentClass = "text-secondary"
}: { 
  icon: React.ElementType; 
  title: string; 
  subtitle: string;
  features: string[];
  accentClass?: string;
}) {
  return (
    <div className="bg-card rounded-3xl p-8 border border-border/30 shadow-soft hover:shadow-medium transition-all duration-300">
      <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-5">
        <Icon className={`w-7 h-7 ${accentClass}`} />
      </div>
      <h3 className="font-heading text-xl md:text-2xl font-bold mb-2 text-foreground">{title}</h3>
      <p className="text-muted-foreground mb-6 leading-relaxed">{subtitle}</p>
      <ul className="space-y-3">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle2 className={`w-5 h-5 ${accentClass} flex-shrink-0 mt-0.5`} />
            <span className="text-foreground text-sm leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Step component for "How it works"
function StepCard({ 
  number, 
  title, 
  description 
}: { 
  number: number; 
  title: string; 
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-secondary text-secondary-foreground font-heading font-bold text-xl flex items-center justify-center mx-auto mb-4">
        {number}
      </div>
      <h3 className="font-heading font-semibold text-lg mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}

// Trust badge component
function TrustBadge({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex items-center gap-3 bg-card rounded-2xl px-5 py-3 border border-border/50 shadow-soft">
      <Icon className="w-5 h-5 text-secondary flex-shrink-0" />
      <span className="text-sm font-medium text-foreground">{text}</span>
    </div>
  );
}

export default function Landing() {
  return (
    <>
      <MetaTags
        title="Dino Reads - Your reading journey starts here"
        description="Track your reading, share your journey, stay accountable to your goals, and discover books you'll genuinely love. No pressure. No noise. Just the joy of reading."
      />
    <div className="min-h-screen bg-background w-full max-w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <DinoLogo size={36} variant="full" />
          <div className="flex items-center gap-4">
            <Link to="/auth?mode=signin">
              <Button variant="ghost" className="font-medium">
                Log in
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-gradient-warm opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Mascot */}
            <div className="flex flex-col items-center mb-8">
              <DinoMascot className="w-40 h-36 md:w-48 md:h-44 float-gentle" />
              <p className="text-muted-foreground text-sm mt-3 font-medium">
                Your friendly guide through every chapter
              </p>
            </div>
            
            {/* Headline */}
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 text-balance leading-tight">
              Your reading journey<br className="hidden sm:block" />{' '}
              starts here.
            </h1>
            
            {/* Sub-headline */}
            <p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-4 font-medium">
              Track progress. Share discoveries. Find your next favourite.
            </p>
            
            {/* Subheadline */}
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 text-pretty leading-relaxed">
              Dino Reads is your supportive reading companion — helping you set gentle goals, see how your taste evolves, and discover books you'll genuinely love. No pressure. No noise. Just the joy of reading.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth">
                <Button size="lg" className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6">
                  Start your reading journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#pillars">
                <Button size="lg" variant="outline" className="btn-pill text-lg px-8 py-6 border-2">
                  See what Dino Reads does
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Statement */}
      <section className="py-8 bg-card/30">
        <div className="container mx-auto px-4">
          <JourneyStatement>
            Reading is a journey. Dino Reads walks it with you.
          </JourneyStatement>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section id="pillars" className="py-20 bg-background scroll-mt-20">
        <div className="container mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
            Everything you need to enjoy reading more
          </h2>
          <p className="text-muted-foreground text-center mb-14 max-w-xl mx-auto">
            Four simple ways Dino Reads supports your reading life.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <PillarCard
              icon={BookOpen}
              title="See where you've been"
              subtitle="Track your reading without the guilt."
              features={[
                "Log books you're reading, finished, or decided to put down",
                "Watch your library grow over weeks, months, and years",
                "Reflect on your journey without productivity pressure"
              ]}
            />
            <PillarCard
              icon={Users}
              title="Read together, your way"
              subtitle="Share your journey with readers who get it."
              features={[
                "Share what you're reading with friends who understand",
                "Compare journeys, not scores — no competition, just connection",
                "Keep sharing optional — your reading is yours"
              ]}
            />
            <PillarCard
              icon={Target}
              title="Gentle nudges, real progress"
              subtitle="Stay accountable without the streak anxiety — or turn goals off entirely."
              accentClass="text-primary"
              features={[
                "Set light-touch goals that fit your reading life — or skip them",
                "Goals are 100% optional and can be toggled off anytime",
                "Get encouragement, not guilt — we're here to help you keep going"
              ]}
            />
            <PillarCard
              icon={Compass}
              title="Discover books you'll actually love"
              subtitle="Find your next read with confidence."
              features={[
                "Recommendations based on your real taste, not algorithms",
                "Your past reads inform smarter suggestions",
                "Feel confident choosing what's next"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Why the dinosaur? */}
      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
              <span className="text-3xl">🦕</span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Why the dinosaur?
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Dinosaurs represent evolution, long journeys, and rediscovery. Your reading habits change over time — the genres you loved at 20 might bore you at 30, and vice versa.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Dino Reads helps you see that transformation. Think of our mascot as a friendly guide who's been on long journeys too — here to walk alongside you, not to push you.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-background scroll-mt-20">
        <div className="container mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
            Get started in minutes
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto">
            No commitment. No credit card. Just start your reading journey.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <StepCard
              number={1}
              title="Join the journey"
              description="Create a free account in seconds."
            />
            <StepCard
              number={2}
              title="Bring your history"
              description="Import from Goodreads or start fresh today."
            />
            <StepCard
              number={3}
              title="Make it yours"
              description="Toggle goals on or off — tailor Dino to how you read."
            />
            <StepCard
              number={4}
              title="Explore and enjoy"
              description="Discover stats, recommendations, and friendly readers."
            />
          </div>
          
          <div className="text-center mt-12">
            <Link to="/auth">
              <Button size="lg" className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6">
                Start your reading journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust & Reassurance */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
            Built for readers, by readers
          </h2>
          <p className="text-muted-foreground text-center mb-10 max-w-xl mx-auto">
            We're real people who love books. Here's what you should know:
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            <TrustBadge icon={Sparkles} text="Built for readers, not algorithms" />
            <TrustBadge icon={Sliders} text="Fully customizable — your way" />
            <TrustBadge icon={Users} text="Social features are optional" />
            <TrustBadge icon={Heart} text="No pressure to perform" />
            <TrustBadge icon={Shield} text="Your journey is yours" />
            <TrustBadge icon={CheckCircle2} text="Free to use, always" />
          </div>
          
          <p className="text-center text-muted-foreground mt-10 max-w-lg mx-auto leading-relaxed">
            This is a place to enjoy reading more, not optimise it to death. Take your time. Read what you love. We'll be here.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-secondary/10">
        <div className="container mx-auto px-4 text-center">
          <DinoMascot className="w-24 h-22 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to make reading feel good again?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
            Join readers who track their progress, share their journeys, and rediscover why they fell in love with books.
          </p>
          <Link to="/auth">
            <Button size="lg" className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-10 py-6">
              Start your reading journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
              <div>
                <DinoLogo size={32} variant="full" className="mb-3" />
                <p className="text-muted-foreground text-sm max-w-xs">
                  Your playful reading companion. Track books, discover your taste, and have more fun with reading.
                </p>
              </div>
              
              <div className="flex flex-col gap-2 text-sm">
                <a href="mailto:hello@dinoreads.com" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  hello@dinoreads.com
                </a>
              </div>
            </div>
            
            <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Dino Reads. All rights reserved.
              </p>
              <nav className="flex flex-wrap justify-center gap-6 text-sm">
                <Link to="/community-guidelines" className="text-muted-foreground hover:text-foreground transition-colors">
                  Community Guidelines
                </Link>
                <Link to="/terms-of-service" className="text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
              </nav>
            </div>
            
            <p className="text-center text-xs text-muted-foreground mt-6">
              🦕 Early access — Dino Reads is still growing.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
